import { css } from '@emotion/css';
import { Colors } from '../../../shared/styles/colors';
import { Layout } from '../../../shared/components/layout/Layout';
import { Vehicle } from '../../../shared/types/server-types';
import { h1Style, valueStyle } from '../../../shared/styles/text';
import { useUser } from '../../../shared/providers/user-provider';
import {
  Button,
  ButtonSizeEnum,
  ButtonVariantEnum,
} from '../../../shared/components/Button';
import { PlusCircleIcon } from '@heroicons/react/24/solid';
import {
  UserRole,
  VehicleTypeEnum,
  vehicleTypeLabels,
} from '../../../shared/types/enums';
import { useState } from 'react';
import { EditVehicleForm } from './EditVehicleForm';
import { Modal } from './Modal';
import { NewVehicleForm } from './NewVehicleForm';
import 'react-toastify/ReactToastify.css';
import { ChevronLeft } from '../../../shared/assets/icons';
import { useNavigator } from '../../../routing/navigator';
import { NoVehiclesYet } from '../../../shared/components/NoVehiclesYet';
import { BOTTOM_NAV_SAFE_HEIGHT } from '../../../shared/styles/css-values';
import { Motion, Radius, unstyledButton } from '../../../shared/styles/tokens';
import { ChevronRightIcon } from '@heroicons/react/24/outline';

export const VehiclePage = (): JSX.Element => {
  const navigator = useNavigator();
  const user = useUser();
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [modalContent, setModalContent] = useState<JSX.Element>(<></>);
  const { vehicles } = user!;
  const canEdit = !user?.charter;

  const handleModal = (visible: boolean, content: JSX.Element) => {
    setModalContent(content);
    setModalVisible(visible);
  };

  return (
    <Layout.Content className={vehiclePageStyle}>
      <Button
        startIcon={<ChevronLeft />}
        variant={ButtonVariantEnum.TEXT}
        size={ButtonSizeEnum.LARGE}
        text={`account`}
        className={backButtonStyle}
        onClick={navigator.toPrevious}
      />
      <div className={vehiclePageHeaderStyle}>
        <h1>Vehicles</h1>
        {user?.role !== UserRole.CHARTER_EMPLOYEE && (
          <span>
            <Button
              size={ButtonSizeEnum.MEDIUM}
              variant={ButtonVariantEnum.TEXT}
              text={'new'}
              startIcon={<PlusCircleIcon className={iconStyle} />}
              onClick={() =>
                handleModal(
                  true,
                  <NewVehicleForm setModalVisible={setModalVisible} />,
                )
              }
            />
          </span>
        )}
      </div>
      {vehicles.length === 0 ? (
        <NoVehiclesYet />
      ) : (
        <ul className={vehiclesListStyle}>
          {vehicles.map((item: Vehicle, index: number) => (
            <li key={index}>
              <button
                type="button"
                className={listItemButtonStyle}
                disabled={!canEdit}
                aria-label={
                  canEdit
                    ? `Edit vehicle ${item.registration}`
                    : item.registration
                }
                onClick={() =>
                  canEdit &&
                  handleModal(
                    true,
                    <EditVehicleForm
                      vehicle={item}
                      setModalVisible={setModalVisible}
                    />,
                  )
                }
              >
                <div className={listItemTextStyle}>
                  <span className={vehicleRegistrationStye}>
                    {item.registration}
                  </span>
                  <span className={vehicleTypeStyle}>
                    {vehicleTypeLabels[item.type as VehicleTypeEnum]}
                  </span>
                </div>
                {canEdit && <ChevronRightIcon className={chevronStyle} />}
              </button>
            </li>
          ))}
        </ul>
      )}
      {/*
          This conditional rendering is needed because modal component uses
          custom hook that adds event listener when mounted,
          this produces a sideffect where modal can't be opened
       */}
      {modalVisible && (
        <Modal visible={modalVisible} setVisible={setModalVisible}>
          {modalContent}
        </Modal>
      )}
    </Layout.Content>
  );
};

const vehiclePageStyle = css`
  padding-top: 0.625rem;
  padding-bottom: calc(${BOTTOM_NAV_SAFE_HEIGHT} + 2rem);
`;

const vehiclePageHeaderStyle = css`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  h1 {
    ${h1Style}
    margin: 0.5rem 0 1.5rem;
  }
`;

const vehiclesListStyle = css`
  list-style: none;
  display: flex;
  flex-direction: column;
  border: 1px solid ${Colors.BORDER};
  border-radius: ${Radius.lg};
  overflow: hidden;

  > li + li {
    border-top: 1px solid ${Colors.BORDER};
  }
`;

const listItemButtonStyle = css`
  ${unstyledButton}
  ${valueStyle}
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  transition: background-color ${Motion.fast};

  &:disabled {
    cursor: default;
  }

  @media (hover: hover) {
    &:hover:not(:disabled) {
      background: ${Colors.SURFACE};
    }
  }

  &:focus-visible {
    outline: 2px solid ${Colors.CAPRI_BLUE};
    outline-offset: -2px;
  }
`;

const listItemTextStyle = css`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const vehicleRegistrationStye = css`
  ${valueStyle}
  margin-bottom: 0.25rem;
  font-weight: bold;
  overflow-wrap: anywhere;
`;

const vehicleTypeStyle = css`
  ${valueStyle}
  color: ${Colors.TEXT_SECONDARY};
`;

const iconStyle = css`
  width: 24px;
  height: 24px;
  color: ${Colors.CAPRI_BLUE};
`;

const chevronStyle = css`
  width: 20px;
  height: 20px;
  flex: 0 0 auto;
  color: ${Colors.WINDOW_GRAY};
`;

const backButtonStyle = css`
  padding-top: 0rem;
`;
